




























































































import { Component, Vue, Prop } from 'vue-property-decorator';
import BasePaginatorHoc from '@/components/base/BasePaginatorHoc.vue';
import {
  clone,
  isLogoAttribute,
  isTitleAttribute
} from '@/jbi-shared/util/group-level-attributes.util';
import ExistingGroupLevelAttributesModal from '@/views/components/PaginatedGroupLevelAttributes/ExistingGroupLevelAttributesModal.vue';
import CreateNewGroupLevelAttributeModal from './CreateNewGroupLevelAttributeModal.vue';
import AttributesList from '@/views/components/PaginatedGroupLevelAttributes/AttributesList.vue';
import store, { RootState } from '@/store/store';
import {
  SortOrder,
  SortSpecs,
  PaginatorSpecs
} from '@/store/types/general.types';
import {
  PermissionsMatrixActionsEnum,
  ResourceExceptions
} from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';
import { isUserAllowed } from '@/utils/rbac.util';
import { EntityTypes } from '@/store/modules/module-tree/enums/module-tree.enums';
import {
  GroupLevelAttributeValue,
  GroupLevelAttributeWithSpec,
  GroupLevelAttributeTemplateMapStatus
} from '@/jbi-shared/types/jaas-group-level-attributes.types';
import {
  InitialActiveDeletedAttributes,
  GroupLevelAttributeType
} from '@/store/modules/admin/types/group-level-attribute.types';
import { Action, State } from 'vuex-class';

@Component({
  computed: {
    PermissionsMatrixActionsEnum() {
      return PermissionsMatrixActionsEnum;
    }
  },
  components: {
    BasePaginatorHoc,
    AttributesList
  }
})
export default class ManageGroupLevelAttributeModal extends Vue {
  @Prop() selectedAttributes!: GroupLevelAttributeValue[];
  @Prop(String) groupName!: string;
  @Prop() groupTypeName!: string;
  @Prop(Number) groupId!: number;
  @Prop() groupExceptions!: ResourceExceptions;

  @State(({ admin }: RootState) => admin.groupLevelAttributeTypes)
  allAttributeTypes!: GroupLevelAttributeType[] | undefined;

  @Action('admin/getGroupLevelAttributeTypes')
  getGroupLevelAttributeTypes!: () => void;

  perPage: number = 50;
  page: number = 1;
  sortColumn: string = 'attributeLabel';
  sortOrder: SortOrder = SortOrder.ASC;

  clonedSelection: GroupLevelAttributeValue[] = [];
  availableSelection: GroupLevelAttributeValue[] = [];
  deletedSelection: GroupLevelAttributeValue[] = [];

  public isUserAllowed(
    action: PermissionsMatrixActionsEnum,
    module: string,
    skipImplicitCheck?: boolean
  ): boolean {
    return isUserAllowed(
      action,
      module,
      EntityTypes.GROUP,
      this.groupTypeName,
      this.groupId,
      this.groupExceptions,
      skipImplicitCheck
    );
  }

  mounted() {
    this.clonedSelection = clone(this.selectedAttributes);
    this.availableSelection = clone(this.selectedAttributes);
    this.getGroupLevelAttributeTypes();
  }

  get AttributesList() {
    return AttributesList;
  }

  get filteredGroupAttributes(): GroupLevelAttributeValue[] {
    const startItemIndex = this.perPage * (this.page - 1);
    const endItemIndex = startItemIndex + this.perPage;
    return this.availableSelection.slice(startItemIndex, endItemIndex);
  }

  get totalCount(): number {
    return this.availableSelection.length;
  }

  disableRowFn(item: GroupLevelAttributeWithSpec): boolean {
    return isTitleAttribute(item) || isLogoAttribute(item);
  }

  selectAllInCurrentPage() {
    const selectedAttrIds = this.clonedSelection.map((spec) => spec.id);
    const attrsToAdd: GroupLevelAttributeValue[] = [];

    this.filteredGroupAttributes
      .filter((currPageSpec) => !this.disableRowFn(currPageSpec))
      .forEach((spec) => {
        if (!selectedAttrIds.includes(spec.id)) {
          attrsToAdd.push(spec);
        }
      });

    this.clonedSelection = [...this.clonedSelection, ...attrsToAdd];
  }

  removeAllInCurrentPage() {
    const currPageAttrIds = this.filteredGroupAttributes.map((spec) => spec.id);

    const attrsToRetain = this.clonedSelection.filter((spec) => {
      const attrInOtherPage = !currPageAttrIds.includes(spec.id);
      const shouldRetain = this.disableRowFn(spec);
      return attrInOtherPage || shouldRetain;
    });

    this.clonedSelection = clone(attrsToRetain);
  }

  addAttribute(item: GroupLevelAttributeValue) {
    this.updateRowSpecVisual(item);
    this.clonedSelection.push(item);
    // Remove from deleted if it was there
    this.deletedSelection = this.deletedSelection.filter(
      (d) => d.id !== item.id
    );
  }

  removeAttribute(item: GroupLevelAttributeValue) {
    this.updateRowSpecVisual(item);
    // Remove ALL instances of this attribute ID to handle existing duplicates in state
    const removedItems = this.clonedSelection.filter((s) => s.id === item.id);
    this.clonedSelection = this.clonedSelection.filter((s) => s.id !== item.id);

    removedItems.forEach((removed) => {
      this.deletedSelection.push({
        ...removed,
        mapStatus: GroupLevelAttributeTemplateMapStatus.DELETE,
        groupLevelAttributeSpec: {
          ...removed.groupLevelAttributeSpec,
          order: 0
        }
      });
    });
  }

  updateRowSpecVisual(updatedSpec: GroupLevelAttributeValue) {
    const index = this.availableSelection.findIndex(
      (s) => s.id === updatedSpec.id
    );
    if (index > -1) {
      this.availableSelection.splice(index, 1, updatedSpec);
    }

    const selIndex = this.clonedSelection.findIndex(
      (s) => s.id === updatedSpec.id
    );
    if (selIndex > -1) {
      this.clonedSelection.splice(selIndex, 1, updatedSpec);
    }
  }

  openAddExistingAttributesModal() {
    this.$buefy.modal.open({
      parent: this,
      component: ExistingGroupLevelAttributesModal,
      hasModalCard: true,
      trapFocus: true,
      fullScreen: true,
      props: {
        hideBaseAttributes: true,
        selectedAttributes: this.clonedSelection,
        deletedAttributes: this.deletedSelection,
        groupId: this.groupId,
        groupTypeName: this.groupTypeName
      },
      events: {
        updateSelection: (data: InitialActiveDeletedAttributes) => {
          // This matches the updateSelection event from ExistingGroupLevelAttributesModal
          // active contains newly added ones
          const activeValues: GroupLevelAttributeValue[] = data.active.map(
            (a) => ({
              ...a,
              value: a.option,
              hasFieldError: false,
              hasDuplicationError: false,
              isTitle: false,
              isDisabled: false
            })
          );
          this.clonedSelection = [...this.clonedSelection, ...activeValues];
          this.availableSelection = [
            ...this.availableSelection,
            ...activeValues
          ];
          // Handle deleted ones if any
          data.deleted.forEach((d) => {
            const idx = this.clonedSelection.findIndex((s) => s.id === d.id);
            if (idx > -1) {
              this.clonedSelection.splice(idx, 1);
            }
            const aIdx = this.availableSelection.findIndex(
              (s) => s.id === d.id
            );
            if (aIdx > -1) {
              this.availableSelection.splice(aIdx, 1);
            }
            this.deletedSelection.push(
              (d as unknown) as GroupLevelAttributeValue
            );
          });
        }
      }
    });
  }

  openCreateAttributeModal() {
    this.$buefy.modal.open({
      parent: this,
      component: CreateNewGroupLevelAttributeModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        allAttributeTypes: this.allAttributeTypes
      },
      events: {
        addNewAttribute: (attribute: GroupLevelAttributeWithSpec) => {
          const newValue: GroupLevelAttributeValue = {
            ...attribute,
            value: attribute.option,
            mapStatus: GroupLevelAttributeTemplateMapStatus.ACTIVE,
            hasFieldError: false,
            hasDuplicationError: false,
            isTitle: false,
            isDisabled: false
          };
          this.clonedSelection.push(newValue);
          this.availableSelection.push(newValue);
        }
      }
    });
  }

  updateAttributes() {
    this.$emit(
      'updateGroupLevelAttributes',
      this.clonedSelection,
      this.deletedSelection
    );
    this.closeModal();
  }

  closeModal() {
    this.$emit('close');
  }

  sortItems(sortEvent: SortSpecs) {
    this.sortColumn = sortEvent.sortColumn;
    this.sortOrder = sortEvent.sortOrder;

    this.availableSelection.sort((a, b) => {
      const valA = (a.label || '').toLowerCase();
      const valB = (b.label || '').toLowerCase();
      if (this.sortOrder === SortOrder.ASC) {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });
  }

  paginateItems(paginator: PaginatorSpecs) {
    this.page = paginator.page;
    this.perPage = paginator.perPage;
  }
}
